import React, { useEffect, useState, useCallback } from 'react';
import {
  adminGetStockOverview, adminGetPurchases, adminCreatePurchase, adminDeletePurchase,
  adminGetStocktakes, adminCreateStocktake, adminGetExpenses, adminCreateExpense, adminDeleteExpense,
  adminUpdateSettings,
  adminGetContributors, adminCreateContributor, adminDeleteContributor,
  adminGetReimbursements, adminCreateReimbursement, adminDeleteReimbursement, adminGetReimbursementLedger,
  adminGetUsage, adminCreateUsage, adminDeleteUsage,
} from '../../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Box } from './AdminUI';
import { useToast } from '../../hooks/use-toast';
import { RefreshCw, PackagePlus, ClipboardList, Wallet, Trash2, TrendingUp, AlertTriangle, CheckCircle2, ShoppingCart, Plus, Save, MessageCircle, Users, HandCoins } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Metric = ({ label, value, accent, testid, sub }) => (
  <div data-testid={testid} className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
    <div className="text-[10px] tracking-[0.2em] uppercase text-alp-gray">{label}</div>
    <div className={`text-2xl mt-2 font-serif ${accent || 'text-alp-cream'}`}>{value}</div>
    {sub ? <div className="text-alp-gray text-xs mt-1">{sub}</div> : null}
  </div>
);

export default function StockPanel() {
  const { toast } = useToast();
  const [overview, setOverview] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [stocktakes, setStocktakes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [ledger, setLedger] = useState({ rows: [], grandOutstanding: 0, grandContributed: 0, grandReimbursed: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // forms
  const [counts, setCounts] = useState({});
  const [stNote, setStNote] = useState('');
  const [stSaving, setStSaving] = useState(false);
  const [pur, setPur] = useState({ kind: 'product', productId: '', qty: '', unitCost: '', feeType: 'Alibaba', amount: '', shop: '', description: '', productCost: '', shipping: '', alibaba: '', note: '', paidBy: '' });
  const [exp, setExp] = useState({ label: '', category: 'Overhead', amount: '', note: '', paidBy: '' });
  const [cfg, setCfg] = useState({ perOrderCost: '', reorderPoint: '', reorderTarget: '', supplierWhatsapp: '' });
  const [contribForm, setContribForm] = useState({ name: '', note: '' });
  const [reimForm, setReimForm] = useState({ contributorId: '', amount: '', note: '' });
  const [usage, setUsage] = useState([]);
  const [useForm, setUseForm] = useState({ purchaseId: '', qty: '', consumedBy: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, p, s, e, c, r, lg, u] = await Promise.all([
        adminGetStockOverview(), adminGetPurchases(), adminGetStocktakes(), adminGetExpenses(),
        adminGetContributors(), adminGetReimbursements(), adminGetReimbursementLedger(), adminGetUsage(),
      ]);
      setOverview(o); setPurchases(p); setStocktakes(s); setExpenses(e);
      setContributors(c); setReimbursements(r); setLedger(lg); setUsage(u);
      setCfg({ perOrderCost: o.settings.perOrderCost, reorderPoint: o.settings.reorderPoint, reorderTarget: o.settings.reorderTarget, supplierWhatsapp: o.settings.supplierWhatsapp || '' });
      const cmap = {}; (o.products || []).forEach((r) => { cmap[r.id] = r.inventory; });
      setCounts(cmap);
    } catch (err) {
      toast({ title: 'Failed to load stock data', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const saveStocktake = async () => {
    const lines = (overview?.products || [])
      .filter((r) => Number(counts[r.id]) !== r.inventory && counts[r.id] !== '' && counts[r.id] != null)
      .map((r) => ({ productId: r.id, counted: parseInt(counts[r.id], 10) || 0 }));
    if (lines.length === 0) { toast({ title: 'No changes to save', description: 'Adjust at least one count.' }); return; }
    setStSaving(true);
    try {
      await adminCreateStocktake({ lines, note: stNote });
      toast({ title: 'Stock take saved', description: `${lines.length} product${lines.length === 1 ? '' : 's'} updated.` });
      setStNote('');
      await load();
    } catch (e) { toast({ title: 'Failed to save stock take', variant: 'destructive' }); }
    finally { setStSaving(false); }
  };

  const addPurchase = async () => {
    if (pur.kind === 'fee') {
      if (!(parseFloat(pur.amount) > 0)) { toast({ title: 'Enter a fee amount', variant: 'destructive' }); return; }
    } else if (pur.kind === 'bulk') {
      const t = (parseFloat(pur.productCost) || 0) + (parseFloat(pur.shipping) || 0) + (parseFloat(pur.alibaba) || 0);
      if (!(t > 0)) { toast({ title: 'Enter at least one cost', description: 'Product, shipping or Alibaba fees.', variant: 'destructive' }); return; }
    } else if (!pur.productId || !(parseInt(pur.qty, 10) > 0)) {
      toast({ title: 'Pick a product and quantity', variant: 'destructive' }); return;
    }
    try {
      await adminCreatePurchase({
        kind: pur.kind, productId: pur.productId, qty: parseInt(pur.qty, 10) || 0, unitCost: parseFloat(pur.unitCost) || 0,
        feeType: pur.feeType, amount: parseFloat(pur.amount) || 0,
        shop: pur.shop, description: pur.description, productCost: parseFloat(pur.productCost) || 0,
        shipping: parseFloat(pur.shipping) || 0, alibaba: parseFloat(pur.alibaba) || 0,
        note: pur.note, paidBy: pur.paidBy,
      });
      const msg = pur.kind === 'fee' ? { title: 'Cost recorded', description: 'Added as a standalone cost.' }
        : pur.kind === 'bulk' ? { title: 'Bulk purchase recorded', description: 'Logged as a single payment.' }
        : { title: 'Purchase recorded', description: 'Inventory updated automatically.' };
      toast(msg);
      setPur({ kind: pur.kind, productId: '', qty: '', unitCost: '', feeType: pur.feeType, amount: '', shop: '', description: '', productCost: '', shipping: '', alibaba: '', note: '', paidBy: '' });
      await load();
    } catch (e) { toast({ title: 'Failed to record', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const removePurchase = async (id) => {
    try { await adminDeletePurchase(id); toast({ title: 'Purchase removed', description: 'Stock reversed.' }); await load(); }
    catch (e) { toast({ title: 'Failed to remove', variant: 'destructive' }); }
  };

  const addExpense = async () => {
    if (!exp.label.trim()) { toast({ title: 'Add an expense name', variant: 'destructive' }); return; }
    try {
      await adminCreateExpense({ label: exp.label, category: exp.category, amount: parseFloat(exp.amount) || 0, note: exp.note, paidBy: exp.paidBy });
      toast({ title: 'Expense added' });
      setExp({ label: '', category: 'Overhead', amount: '', note: '', paidBy: '' });
      await load();
    } catch (e) { toast({ title: 'Failed to add expense', variant: 'destructive' }); }
  };

  const addContributor = async () => {
    if (!contribForm.name.trim()) { toast({ title: 'Enter a name', variant: 'destructive' }); return; }
    try {
      await adminCreateContributor({ name: contribForm.name, note: contribForm.note });
      toast({ title: 'Contributor added' });
      setContribForm({ name: '', note: '' });
      await load();
    } catch (e) { toast({ title: 'Failed to add contributor', variant: 'destructive' }); }
  };

  const removeContributor = async (id) => {
    try { await adminDeleteContributor(id); toast({ title: 'Contributor removed' }); await load(); }
    catch (e) { toast({ title: 'Failed to remove', variant: 'destructive' }); }
  };

  const addReimbursement = async () => {
    if (!reimForm.contributorId || !(parseFloat(reimForm.amount) > 0)) { toast({ title: 'Pick a person and amount', variant: 'destructive' }); return; }
    try {
      await adminCreateReimbursement({ contributorId: reimForm.contributorId, amount: parseFloat(reimForm.amount), note: reimForm.note });
      toast({ title: 'Reimbursement logged' });
      setReimForm({ contributorId: '', amount: '', note: '' });
      await load();
    } catch (e) { toast({ title: 'Failed to log reimbursement', variant: 'destructive' }); }
  };

  const removeReimbursement = async (id) => {
    try { await adminDeleteReimbursement(id); toast({ title: 'Reimbursement removed' }); await load(); }
    catch (e) { toast({ title: 'Failed to remove', variant: 'destructive' }); }
  };

  const purchaseRemaining = (purchaseId) => {
    const p = purchases.find((x) => x.id === purchaseId);
    if (!p) return 0;
    const used = usage.filter((u) => u.purchaseId === purchaseId).reduce((s, u) => s + (u.qty || 0), 0);
    return (p.qty || 0) - used;
  };
  const usablePurchases = purchases.filter((p) => p.paidBy && purchaseRemaining(p.id) > 0);

  const addUsage = async () => {
    if (!useForm.purchaseId || !(parseInt(useForm.qty, 10) > 0)) { toast({ title: 'Pick a purchase and quantity', variant: 'destructive' }); return; }
    try {
      await adminCreateUsage({ purchaseId: useForm.purchaseId, qty: parseInt(useForm.qty, 10), consumedBy: useForm.consumedBy });
      toast({ title: 'Product usage logged', description: 'Value applied against what the payer is owed.' });
      setUseForm({ purchaseId: '', qty: '', consumedBy: '' });
      await load();
    } catch (e) { toast({ title: 'Failed to log usage', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const removeUsage = async (id) => {
    try { await adminDeleteUsage(id); toast({ title: 'Usage removed', description: 'Stock restored.' }); await load(); }
    catch (e) { toast({ title: 'Failed to remove', variant: 'destructive' }); }
  };

  const removeExpense = async (id) => {
    try { await adminDeleteExpense(id); toast({ title: 'Expense removed' }); await load(); }
    catch (e) { toast({ title: 'Failed to remove', variant: 'destructive' }); }
  };

  const saveCfg = async () => {
    try {
      await adminUpdateSettings({
        perOrderCost: parseFloat(cfg.perOrderCost) || 0,
        reorderPoint: parseInt(cfg.reorderPoint, 10) || 0,
        reorderTarget: parseInt(cfg.reorderTarget, 10) || 0,
        supplierWhatsapp: cfg.supplierWhatsapp || '',
      });
      toast({ title: 'Settings saved' });
      await load();
    } catch (e) { toast({ title: 'Failed to save settings', variant: 'destructive' }); }
  };

  if (!overview) return <p className="text-alp-gray py-16 text-center" data-testid="stock-loading">Loading stock &amp; finance&hellip;</p>;

  const { forecast, costs, revenue, profit, breakEven, toOrder, products } = overview;

  return (
    <div className="space-y-8" data-testid="stock-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl text-alp-cream">Stock Take &amp; Finance</h3>
          <p className="text-alp-gray text-sm">Track stock, spend and profit. Stock auto-drops when an order is marked <span className="text-alp-cream">Payment Confirmed</span>.</p>
        </div>
        <button onClick={load} data-testid="stock-refresh" className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors"><RefreshCw size={13} /> Refresh</button>
      </div>

      {/* Break-even banner */}
      <div data-testid="breakeven-banner" className={`rounded-lg p-5 border ${breakEven.covered ? 'border-emerald-400/40 bg-emerald-400/5' : 'border-alp-gold/40 bg-alp-gold/5'}`}>
        <div className="flex items-center gap-3">
          {breakEven.covered ? <CheckCircle2 className="text-emerald-400" size={22} /> : <AlertTriangle className="text-alp-gold" size={22} />}
          <div>
            {breakEven.covered ? (
              <p className="text-alp-cream text-lg">Expenses covered — you're <span className="text-emerald-400 font-semibold">{money(breakEven.netPosition)}</span> ahead.</p>
            ) : (
              <p className="text-alp-cream text-lg">You need <span className="text-alp-gold font-semibold">{money(Math.abs(breakEven.netPosition))}</span> more in sales to break even.</p>
            )}
            <p className="text-alp-gray text-xs mt-1">Money in {money(breakEven.moneyIn)} &minus; money out {money(breakEven.moneyOut)} (stock bought + packaging + other expenses).</p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-alp-card/60 border border-alp-line flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="stock-tab-overview" className="data-[state=active]:bg-alp-gold data-[state=active]:text-alp-black text-alp-cream"><TrendingUp size={14} className="mr-2" /> Overview</TabsTrigger>
          <TabsTrigger value="stocktake" data-testid="stock-tab-stocktake" className="data-[state=active]:bg-alp-gold data-[state=active]:text-alp-black text-alp-cream"><ClipboardList size={14} className="mr-2" /> Stock Take</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="stock-tab-purchases" className="data-[state=active]:bg-alp-gold data-[state=active]:text-alp-black text-alp-cream"><PackagePlus size={14} className="mr-2" /> Purchases</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="stock-tab-expenses" className="data-[state=active]:bg-alp-gold data-[state=active]:text-alp-black text-alp-cream"><Wallet size={14} className="mr-2" /> Expenses</TabsTrigger>
          <TabsTrigger value="reimburse" data-testid="stock-tab-reimburse" className="data-[state=active]:bg-alp-gold data-[state=active]:text-alp-black text-alp-cream"><HandCoins size={14} className="mr-2" /> Reimbursements</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-6 space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric testid="metric-revenue" label="Revenue (paid orders)" value={money(revenue.paidRevenue)} accent="text-alp-gold" sub={`${revenue.paidOrderCount} paid of ${revenue.allOrderCount} orders`} />
            <Metric testid="metric-spent" label="Total spent" value={money(costs.totalCashOut)} sub="Stock + packaging + expenses" />
            <Metric testid="metric-netprofit" label="Net profit (sold)" value={money(profit.netProfit)} accent={profit.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'} sub={`Gross ${money(profit.grossProfit)}`} />
            <Metric testid="metric-position" label="Net position" value={money(breakEven.netPosition)} accent={breakEven.covered ? 'text-emerald-400' : 'text-alp-gold'} sub="After all money out" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
              <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-4">Costs spent</h4>
              <Row label="Stock purchased (invested)" value={money(costs.totalInvestedInStock)} testid="cost-invested" />
              <Row label="Cost of goods sold (COGS)" value={money(costs.cogsSold)} testid="cost-cogs" />
              <Row label={`Packaging (${money(overview.settings.perOrderCost)} x ${revenue.paidOrderCount})`} value={money(costs.packaging)} testid="cost-packaging" />
              <Row label="Other expenses" value={money(costs.otherExpenses)} testid="cost-other" />
              <Row label="Total cash out" value={money(costs.totalCashOut)} bold testid="cost-total" />
            </div>
            <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
              <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-4">Forecast from current stock</h4>
              <Row label="Stock value at cost" value={money(forecast.stockCost)} testid="fc-cost" />
              <Row label="Potential revenue (retail)" value={money(forecast.stockRetail)} accent="text-alp-gold" testid="fc-retail" />
              <Row label="Potential profit if all sells" value={money(forecast.potentialProfit)} accent="text-emerald-400" bold testid="fc-profit" />
              <p className="text-alp-gray text-xs mt-3">If you sell everything currently on hand.</p>
            </div>
          </div>

          {/* To order next */}
          <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="to-order-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold flex items-center gap-2"><ShoppingCart size={14} /> To order next (stock &le; {overview.settings.reorderPoint})</h4>
              <div className="flex items-center gap-4">
                <span className="text-alp-cream text-sm">Est. cost: <span className="text-alp-gold">{money(toOrder.totalCost)}</span></span>
                {toOrder.items.length > 0 && (() => {
                  const waNum = (overview.settings.supplierWhatsapp || '').replace(/[^0-9]/g, '');
                  const msg = 'Hi, I would like to reorder the following:\n' + toOrder.items.map((r) => {
                    const packs = Math.max(1, Math.ceil(r.reorderQty / 10));
                    return `- ${r.name}${r.size ? ` ${r.size}` : ''}: ${packs} pack${packs === 1 ? '' : 's'} of 10 (${r.reorderQty} vials)`;
                  }).join('\n') + '\nThank you.';
                  const href = `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`;
                  return waNum ? (
                    <a href={href} target="_blank" rel="noreferrer" data-testid="supplier-whatsapp-btn" className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase bg-[#25D366] text-black px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity"><MessageCircle size={14} /> Order from supplier</a>
                  ) : (
                    <span data-testid="supplier-whatsapp-hint" className="text-xs text-alp-gray">Add supplier WhatsApp in settings &darr;</span>
                  );
                })()}
              </div>
            </div>
            {toOrder.items.length === 0 ? (
              <p className="text-alp-gray text-sm">Nothing to reorder — all products are above the reorder point.</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 text-[10px] tracking-[0.15em] uppercase text-alp-gray px-1">
                  <div className="col-span-5">Product</div><div className="col-span-2 text-right">On hand</div><div className="col-span-2 text-right">Order qty</div><div className="col-span-3 text-right">Cost</div>
                </div>
                {toOrder.items.map((r) => (
                  <div key={r.id} data-testid={`to-order-${r.id}`} className="grid grid-cols-12 items-center bg-alp-dark/50 rounded px-3 py-2 text-sm">
                    <div className="col-span-5 text-alp-cream">{r.name} <span className="text-alp-gray text-xs">{r.size}</span></div>
                    <div className={`col-span-2 text-right ${r.inventory === 0 ? 'text-red-400' : 'text-alp-cream'}`}>{r.inventory}</div>
                    <div className="col-span-2 text-right text-alp-gold">{r.reorderQty}</div>
                    <div className="col-span-3 text-right text-alp-cream">{money(r.cost)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="stock-settings-card">
            <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-4">Finance settings</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Box label="Packaging cost per order (AUD)"><Input type="number" value={cfg.perOrderCost} onChange={(e) => setCfg({ ...cfg, perOrderCost: e.target.value })} data-testid="cfg-per-order" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Reorder when stock at/below"><Input type="number" value={cfg.reorderPoint} onChange={(e) => setCfg({ ...cfg, reorderPoint: e.target.value })} data-testid="cfg-reorder-point" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Reorder up to (target)"><Input type="number" value={cfg.reorderTarget} onChange={(e) => setCfg({ ...cfg, reorderTarget: e.target.value })} data-testid="cfg-reorder-target" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
            </div>
            <div className="mt-4">
              <Box label="Supplier WhatsApp number (international format, e.g. 61400000000)"><Input value={cfg.supplierWhatsapp} onChange={(e) => setCfg({ ...cfg, supplierWhatsapp: e.target.value })} data-testid="cfg-supplier-whatsapp" placeholder="Add your supplier's WhatsApp number" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
            </div>
            <button onClick={saveCfg} data-testid="save-cfg" className="mt-4 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Save size={14} /> Save settings</button>
          </div>
        </TabsContent>

        {/* STOCK TAKE */}
        <TabsContent value="stocktake" className="mt-6 space-y-5">
          <p className="text-alp-gray text-sm">Enter your physical count for each product. Saving updates inventory instantly and records the count.</p>
          <div className="border border-alp-line rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 bg-alp-dark/70 text-[10px] tracking-[0.15em] uppercase text-alp-gray px-4 py-2">
              <div className="col-span-6">Product</div><div className="col-span-2 text-right">System</div><div className="col-span-4 text-right">Counted</div>
            </div>
            <div className="max-h-[420px] overflow-y-auto divide-y divide-alp-line/50">
              {products.map((r) => (
                <div key={r.id} data-testid={`stocktake-row-${r.id}`} className="grid grid-cols-12 items-center px-4 py-2.5 text-sm">
                  <div className="col-span-6 text-alp-cream">{r.name} <span className="text-alp-gray text-xs">{r.size}</span></div>
                  <div className="col-span-2 text-right text-alp-gray">{r.inventory}</div>
                  <div className="col-span-4 flex justify-end">
                    <Input type="number" value={counts[r.id] ?? ''} onChange={(e) => setCounts({ ...counts, [r.id]: e.target.value })} data-testid={`stocktake-count-${r.id}`} className="w-24 h-9 bg-alp-dark border-alp-line text-alp-cream text-right" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Box label="Note (optional)"><Input value={stNote} onChange={(e) => setStNote(e.target.value)} placeholder="e.g. End of June count" data-testid="stocktake-note" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
          <button onClick={saveStocktake} disabled={stSaving} data-testid="save-stocktake" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50"><Save size={14} /> {stSaving ? 'Saving\u2026' : 'Save stock take'}</button>

          {stocktakes.length > 0 && (
            <div className="mt-6">
              <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-3">Recent stock takes</h4>
              <div className="space-y-2">
                {stocktakes.slice(0, 8).map((s) => (
                  <div key={s.id} data-testid={`stocktake-history-${s.id}`} className="flex items-center justify-between bg-alp-dark/50 rounded px-4 py-2.5 text-sm">
                    <div className="text-alp-cream">{new Date(s.createdAt).toLocaleString()} <span className="text-alp-gray">&middot; {s.itemCount} items{s.note ? ` · ${s.note}` : ''}</span></div>
                    <div className="text-alp-gray">Value: <span className="text-alp-gold">{money(s.stockValueRetail)}</span> retail</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* PURCHASES */}
        <TabsContent value="purchases" className="mt-6 space-y-5">
          <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
            <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-4">Record a purchase or cost</h4>
            <div className="max-w-xs mb-4">
              <Box label="Type">
                <Select value={pur.kind} onValueChange={(v) => setPur({ ...pur, kind: v })}>
                  <SelectTrigger data-testid="purchase-kind" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Stock (product)</SelectItem>
                    <SelectItem value="fee">Fee / shipping (no product)</SelectItem>
                    <SelectItem value="bulk">Bulk purchase (1 payment)</SelectItem>
                  </SelectContent>
                </Select>
              </Box>
            </div>
            {pur.kind === 'fee' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Box label="Cost type">
                  <Select value={pur.feeType} onValueChange={(v) => setPur({ ...pur, feeType: v })}>
                    <SelectTrigger data-testid="purchase-feetype" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Alibaba', 'Shipping', 'Other'].map((f) => (<SelectItem key={f} value={f}>{f === 'Alibaba' ? 'Alibaba fees' : f}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </Box>
                <Box label="Amount (AUD)"><Input type="number" value={pur.amount} onChange={(e) => setPur({ ...pur, amount: e.target.value })} data-testid="purchase-amount" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Note (optional)"><Input value={pur.note} onChange={(e) => setPur({ ...pur, note: e.target.value })} data-testid="purchase-note" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Paid by (optional)">
                  <Select value={pur.paidBy || 'none'} onValueChange={(v) => setPur({ ...pur, paidBy: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="purchase-paidby" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Who paid?" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not tracked</SelectItem>
                      {contributors.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </Box>
              </div>
            ) : pur.kind === 'bulk' ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Box label="Shop / Supplier"><Input value={pur.shop} onChange={(e) => setPur({ ...pur, shop: e.target.value })} data-testid="bulk-shop" placeholder="e.g. Alibaba supplier name" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Description"><Input value={pur.description} onChange={(e) => setPur({ ...pur, description: e.target.value })} data-testid="bulk-description" placeholder="What was in this order" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Paid by (optional)">
                    <Select value={pur.paidBy || 'none'} onValueChange={(v) => setPur({ ...pur, paidBy: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="bulk-paidby" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Who paid?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not tracked</SelectItem>
                        {contributors.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Box>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <Box label="Cost of product (AUD)"><Input type="number" value={pur.productCost} onChange={(e) => setPur({ ...pur, productCost: e.target.value })} data-testid="bulk-product-cost" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Shipping (AUD)"><Input type="number" value={pur.shipping} onChange={(e) => setPur({ ...pur, shipping: e.target.value })} data-testid="bulk-shipping" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Alibaba fees (AUD)"><Input type="number" value={pur.alibaba} onChange={(e) => setPur({ ...pur, alibaba: e.target.value })} data-testid="bulk-alibaba" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                </div>
                <div className="mt-4 text-sm text-alp-gray">Total payment: <span className="text-alp-gold font-semibold" data-testid="bulk-total">{money((parseFloat(pur.productCost) || 0) + (parseFloat(pur.shipping) || 0) + (parseFloat(pur.alibaba) || 0))}</span></div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Box label="Product">
                    <Select value={pur.productId} onValueChange={(v) => setPur({ ...pur, productId: v })}>
                      <SelectTrigger data-testid="purchase-product" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent>
                        {products.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}{r.size ? ` (${r.size})` : ''}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Box>
                  <Box label="Quantity bought"><Input type="number" value={pur.qty} onChange={(e) => setPur({ ...pur, qty: e.target.value })} data-testid="purchase-qty" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Unit cost (AUD)"><Input type="number" value={pur.unitCost} onChange={(e) => setPur({ ...pur, unitCost: e.target.value })} data-testid="purchase-unit-cost" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Note (optional)"><Input value={pur.note} onChange={(e) => setPur({ ...pur, note: e.target.value })} data-testid="purchase-note" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                </div>
                <div className="mt-4 max-w-xs">
                  <Box label="Paid by (optional)">
                    <Select value={pur.paidBy || 'none'} onValueChange={(v) => setPur({ ...pur, paidBy: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="purchase-paidby" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Who paid?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not tracked</SelectItem>
                        {contributors.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Box>
                </div>
              </>
            )}
            <button onClick={addPurchase} data-testid="add-purchase" className="mt-4 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={14} /> {pur.kind === 'fee' ? 'Record cost' : pur.kind === 'bulk' ? 'Record bulk purchase' : 'Record purchase'}</button>
          </div>
          {purchases.length === 0 ? (
            <p className="text-alp-gray text-sm py-6 text-center">No purchases logged yet.</p>
          ) : (
            <div className="space-y-2">
              {purchases.map((p) => (
                <div key={p.id} data-testid={`purchase-${p.id}`} className="flex items-center justify-between bg-alp-dark/50 rounded px-4 py-2.5 text-sm">
                  {p.kind === 'fee' ? (
                    <div className="text-alp-cream">{p.feeType === 'Alibaba' ? 'Alibaba fees' : p.feeType || 'Fee'} <span className="text-alp-gray text-xs">· cost</span><div className="text-alp-gray text-xs">{new Date(p.createdAt).toLocaleDateString()}{p.paidByName ? ` · paid by ${p.paidByName}` : ''}{p.note ? ` · ${p.note}` : ''}</div></div>
                  ) : p.kind === 'bulk' ? (
                    <div className="text-alp-cream">{p.shop || 'Bulk purchase'} <span className="text-alp-gray text-xs">· bulk</span>{p.description ? <span className="text-alp-gray text-xs"> · {p.description}</span> : null}<div className="text-alp-gray text-xs">{new Date(p.createdAt).toLocaleDateString()} · goods {money(p.goodsCost)}{p.shipping ? ` · ship ${money(p.shipping)}` : ''}{p.alibaba ? ` · Alibaba ${money(p.alibaba)}` : ''}{p.paidByName ? ` · paid by ${p.paidByName}` : ''}</div></div>
                  ) : (
                    <div className="text-alp-cream">{p.productName} <span className="text-alp-gray text-xs">{p.size}</span><div className="text-alp-gray text-xs">{new Date(p.createdAt).toLocaleDateString()} · +{p.qty} @ {money(p.unitCost)}{p.paidByName ? ` · paid by ${p.paidByName}` : ''}{p.note ? ` · ${p.note}` : ''}</div></div>
                  )}
                  <div className="flex items-center gap-4">
                    <span className="text-alp-gold">{money(p.totalCost)}</span>
                    <button onClick={() => removePurchase(p.id)} data-testid={`del-purchase-${p.id}`} className="text-alp-gray hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* EXPENSES */}
        <TabsContent value="expenses" className="mt-6 space-y-5">
          <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
            <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-4">Log an expense (website, marketing, equipment&hellip;)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Box label="What was it?"><Input value={exp.label} onChange={(e) => setExp({ ...exp, label: e.target.value })} data-testid="expense-label" placeholder="e.g. Website hosting" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Category">
                <Select value={exp.category} onValueChange={(v) => setExp({ ...exp, category: v })}>
                  <SelectTrigger data-testid="expense-category" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Overhead', 'Marketing', 'Equipment', 'Packaging', 'Shipping', 'Fees', 'Other'].map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Box>
              <Box label="Amount (AUD)"><Input type="number" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} data-testid="expense-amount" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Note (optional)"><Input value={exp.note} onChange={(e) => setExp({ ...exp, note: e.target.value })} data-testid="expense-note" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
            </div>
            <div className="mt-4 max-w-xs">
              <Box label="Paid by (optional)">
                <Select value={exp.paidBy || 'none'} onValueChange={(v) => setExp({ ...exp, paidBy: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="expense-paidby" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Who paid?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not tracked</SelectItem>
                    {contributors.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Box>
            </div>
            <button onClick={addExpense} data-testid="add-expense" className="mt-4 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={14} /> Add expense</button>
          </div>
          {expenses.length === 0 ? (
            <p className="text-alp-gray text-sm py-6 text-center">No expenses logged yet.</p>
          ) : (
            <div className="space-y-2">
              {expenses.map((x) => (
                <div key={x.id} data-testid={`expense-${x.id}`} className="flex items-center justify-between bg-alp-dark/50 rounded px-4 py-2.5 text-sm">
                  <div className="text-alp-cream">{x.label} <span className="text-alp-gray text-xs">· {x.category}{x.paidByName ? ` · paid by ${x.paidByName}` : ''}{x.note ? ` · ${x.note}` : ''}</span><div className="text-alp-gray text-xs">{new Date(x.createdAt).toLocaleDateString()}</div></div>
                  <div className="flex items-center gap-4">
                    <span className="text-alp-gold">{money(x.amount)}</span>
                    <button onClick={() => removeExpense(x.id)} data-testid={`del-expense-${x.id}`} className="text-alp-gray hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* REIMBURSEMENTS */}
        <TabsContent value="reimburse" className="mt-6 space-y-6">
          <div className={`rounded-lg p-5 border ${ledger.grandOutstanding > 0 ? 'border-alp-gold/40 bg-alp-gold/5' : 'border-emerald-400/40 bg-emerald-400/5'}`} data-testid="reimburse-grand">
            <div className="flex items-center gap-3">
              <HandCoins className={ledger.grandOutstanding > 0 ? 'text-alp-gold' : 'text-emerald-400'} size={22} />
              <div>
                <p className="text-alp-cream text-lg">Total owed across everyone: <span className={ledger.grandOutstanding > 0 ? 'text-alp-gold font-semibold' : 'text-emerald-400 font-semibold'}>{money(ledger.grandOutstanding)}</span></p>
                <p className="text-alp-gray text-xs mt-1">Contributed {money(ledger.grandContributed)} &minus; reimbursed {money(ledger.grandReimbursed)} &minus; product used {money(ledger.grandUsed)}.</p>
              </div>
            </div>
          </div>

          {/* Contributors manager */}
          <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
            <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-4 flex items-center gap-2"><Users size={14} /> People / contributors</h4>
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="flex-1 min-w-[180px]"><Box label="Name"><Input value={contribForm.name} onChange={(e) => setContribForm({ ...contribForm, name: e.target.value })} data-testid="contributor-name" placeholder="e.g. Jordan (Partner)" className="bg-alp-dark border-alp-line text-alp-cream" /></Box></div>
              <div className="flex-1 min-w-[180px]"><Box label="Note (optional)"><Input value={contribForm.note} onChange={(e) => setContribForm({ ...contribForm, note: e.target.value })} data-testid="contributor-note" className="bg-alp-dark border-alp-line text-alp-cream" /></Box></div>
              <button onClick={addContributor} data-testid="add-contributor" className="inline-flex items-center gap-2 border border-alp-gold/50 text-alp-gold px-4 py-2.5 rounded text-xs tracking-[0.12em] uppercase hover:bg-alp-gold hover:text-alp-black transition-colors"><Plus size={14} /> Add</button>
            </div>
            {contributors.length === 0 ? (
              <p className="text-alp-gray text-sm">No contributors yet. Add the people who front money for stock or expenses.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {contributors.map((c) => (
                  <span key={c.id} data-testid={`contributor-${c.id}`} className="inline-flex items-center gap-2 text-sm text-alp-cream border border-alp-line rounded px-3 py-1.5">
                    {c.name}{c.note ? <span className="text-alp-gray text-xs">· {c.note}</span> : null}
                    <button onClick={() => removeContributor(c.id)} data-testid={`del-contributor-${c.id}`} className="text-alp-gray hover:text-red-400"><Trash2 size={13} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Ledger */}
          <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="ledger-card">
            <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-4">Reimbursement ledger</h4>
            {ledger.rows.length === 0 ? (
              <p className="text-alp-gray text-sm">No contributions tagged yet. Tag "Paid by" on a purchase or expense.</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 text-[10px] tracking-[0.15em] uppercase text-alp-gray px-1">
                  <div className="col-span-3">Person</div><div className="col-span-2 text-right">Contributed</div><div className="col-span-2 text-right">Reimbursed</div><div className="col-span-2 text-right">Used</div><div className="col-span-3 text-right">Outstanding</div>
                </div>
                {ledger.rows.map((r) => (
                  <div key={r.contributorId} data-testid={`ledger-${r.contributorId}`} className="grid grid-cols-12 items-center bg-alp-dark/50 rounded px-3 py-2.5 text-sm">
                    <div className="col-span-3 text-alp-cream">{r.name}</div>
                    <div className="col-span-2 text-right text-alp-cream">{money(r.contributed)}</div>
                    <div className="col-span-2 text-right text-alp-gray">{money(r.reimbursed)}</div>
                    <div className="col-span-2 text-right text-alp-gray">{money(r.used)}</div>
                    <div className={`col-span-3 text-right font-semibold ${r.outstanding > 0 ? 'text-alp-gold' : 'text-emerald-400'}`}>{money(r.outstanding)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Record reimbursement */}
          <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
            <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-4">Record a reimbursement (money paid back)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Box label="To person">
                <Select value={reimForm.contributorId} onValueChange={(v) => setReimForm({ ...reimForm, contributorId: v })}>
                  <SelectTrigger data-testid="reimburse-person" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Select person" /></SelectTrigger>
                  <SelectContent>
                    {contributors.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Box>
              <Box label="Amount (AUD)"><Input type="number" value={reimForm.amount} onChange={(e) => setReimForm({ ...reimForm, amount: e.target.value })} data-testid="reimburse-amount" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Note (optional)"><Input value={reimForm.note} onChange={(e) => setReimForm({ ...reimForm, note: e.target.value })} data-testid="reimburse-note" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
            </div>
            <button onClick={addReimbursement} data-testid="add-reimbursement" className="mt-4 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={14} /> Log reimbursement</button>
          </div>

          {reimbursements.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold">History</h4>
              {reimbursements.map((r) => (
                <div key={r.id} data-testid={`reimbursement-${r.id}`} className="flex items-center justify-between bg-alp-dark/50 rounded px-4 py-2.5 text-sm">
                  <div className="text-alp-cream">{r.contributorName}<span className="text-alp-gray text-xs"> · {new Date(r.createdAt).toLocaleDateString()}{r.note ? ` · ${r.note}` : ''}</span></div>
                  <div className="flex items-center gap-4">
                    <span className="text-alp-gold">{money(r.amount)}</span>
                    <button onClick={() => removeReimbursement(r.id)} data-testid={`del-reimbursement-${r.id}`} className="text-alp-gray hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Product usage (personal/sample) — comes off the payer's balance */}
          <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="usage-card">
            <h4 className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-1">Log product used (personal / samples)</h4>
            <p className="text-alp-gray text-xs mb-4">Taking stock for yourself or Carole? Log it here — its cost comes off what the person who paid for that purchase is owed, and stock is reduced.</p>
            {usablePurchases.length === 0 ? (
              <p className="text-alp-gray text-sm">No purchases with a "Paid by" tag and stock remaining. Tag "Paid by" on a purchase first.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Box label="From which purchase">
                    <Select value={useForm.purchaseId} onValueChange={(v) => setUseForm({ ...useForm, purchaseId: v })}>
                      <SelectTrigger data-testid="usage-purchase" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Select purchase" /></SelectTrigger>
                      <SelectContent>
                        {usablePurchases.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.productName} · {p.paidByName || 'Unknown'} · {purchaseRemaining(p.id)} left @ {money((p.totalCost || 0) / (p.qty || 1))}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Box>
                  <Box label="Quantity used"><Input type="number" value={useForm.qty} onChange={(e) => setUseForm({ ...useForm, qty: e.target.value })} data-testid="usage-qty" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Used by (optional)">
                    <Select value={useForm.consumedBy || 'none'} onValueChange={(v) => setUseForm({ ...useForm, consumedBy: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="usage-consumedby" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Who took it?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        <SelectItem value="Me">Me</SelectItem>
                        {contributors.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Box>
                </div>
                <button onClick={addUsage} data-testid="add-usage" className="mt-4 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={14} /> Log usage</button>
              </>
            )}
            {usage.length > 0 && (
              <div className="mt-5 space-y-2">
                {usage.map((u) => (
                  <div key={u.id} data-testid={`usage-${u.id}`} className="flex items-center justify-between bg-alp-dark/50 rounded px-4 py-2.5 text-sm">
                    <div className="text-alp-cream">{u.qty} × {u.productName} <span className="text-alp-gray text-xs">· off {u.paidByName || 'Unknown'}{u.consumedBy ? ` · used by ${u.consumedBy}` : ''} · {new Date(u.createdAt).toLocaleDateString()}</span></div>
                    <div className="flex items-center gap-4">
                      <span className="text-alp-gray">−{money(u.value)}</span>
                      <button onClick={() => removeUsage(u.id)} data-testid={`del-usage-${u.id}`} className="text-alp-gray hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Row = ({ label, value, accent, bold, testid }) => (
  <div className={`flex items-center justify-between py-1.5 ${bold ? 'border-t border-alp-line/60 mt-1.5 pt-2.5' : ''}`}>
    <span className={`text-sm ${bold ? 'text-alp-cream' : 'text-alp-gray'}`}>{label}</span>
    <span data-testid={testid} className={`text-sm ${accent || (bold ? 'text-alp-cream font-semibold' : 'text-alp-cream')}`}>{value}</span>
  </div>
);
